import React, { useRef, useState, useEffect } from "react";
import { Card, Button, Form, InputGroup } from "react-bootstrap";
import "./AiChat.scss";
import { IoClose } from "react-icons/io5";
import { IoIosSend } from "react-icons/io";
import { GetListCommonCall, PostCommonAPI } from "../../POS/APIServices/POS_API_Services";
import { useSelector } from "react-redux";
import WebService from "../../Services/WebService";

const aiIcon = () => (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
        {/* SAME SVG */}
    </svg>
);

type ChatMessage = {
    id: string;
    type: "question" | "answer";
    text: string;
};

interface AiChatProps {
    onClose?: () => void;
}

const AiChat: React.FC<AiChatProps> = ({ onClose }) => {
    const Details = useSelector((state: any) => state?.items?.meDetails);

    const [question, setQuestion] = useState("");
    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(false);
    const [messages, setMessages] = useState<ChatMessage[]>([]);

    const chatEndRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages, loading]);

    useEffect(() => {
        if (Details?.id) {
            getChatHistory();
        }
    }, [Details?.id]);

    const mapHistoryToMessages = (history: any[]): ChatMessage[] => {
        return history
            .sort(
                (a, b) =>
                    new Date(a.created_at).getTime() -
                    new Date(b.created_at).getTime()
            )
            .map(item => ({
                id: item.id,
                type: item.from === "STAFF" ? "question" : "answer",
                text: item.message
            }));
    };

    const getChatHistory = () => {
        setInitialLoading(true);
        GetListCommonCall(`ai-chat/history/${Details.id}`, {}, "")
            .then((res: any) => {
                const history = res?.data || res || [];
                const mapped = mapHistoryToMessages(history);
                setMessages(mapped);
                setInitialLoading(false);
            })
            .catch(() => {
                setMessages([]);
            })
            .finally(() => {
                setInitialLoading(false);
            });
    };

    const typeWriterEffect = (
        fullText: string,
        onUpdate: (text: string) => void,
        speed = 120
    ) => {
        const words = fullText.split(" ");
        let index = 0;

        const interval = setInterval(() => {
            if (index < words.length) {
                onUpdate(words.slice(0, index + 1).join(" "));
                index++;
            } else {
                clearInterval(interval);
            }
        }, speed);
    };

    const handleSendMessage = () => {
        if (!question.trim() || loading) return;
        const userQuestion = question;
        setQuestion("");
        setMessages(prev => [
            ...prev,
            {
                id: Date.now().toString(),
                type: "question",
                text: userQuestion
            }
        ]);
        setLoading(true);
        const body = {
            client_id: localStorage.getItem("client_id"),
            outlet_id: localStorage.getItem("outlet_id"),
            question: userQuestion
        };

        WebService.postAPI({
            action: "ai-chat/chat",
            body: body,
            btnId: "",
            isShowError: true
        })
            .then((res: any) => {
                const answerText =
                    res?.answer || "No response available";
                const aiMsgId = Date.now().toString() + "_ai";
                setMessages(prev => [
                    ...prev,
                    {
                        id: aiMsgId,
                        type: "answer",
                        text: ""
                    }
                ]);

                typeWriterEffect(answerText, (partial) => {
                    setMessages(prev =>
                        prev.map(msg =>
                            msg.id === aiMsgId
                                ? { ...msg, text: partial }
                                : msg
                        )
                    );
                });
            }).catch(() => {
                setMessages(prev => [
                    ...prev,
                    {
                        id: Date.now().toString(),
                        type: "answer",
                        text: "No response available"
                    }
                ]);
            }).finally(() => {
                setLoading(false);
            });
    };

    return (
        <div className="ai-chat-backdrop" onClick={() => onClose?.()}  >
            <div className="ai-chat-wrapper"
                onClick={(e) => e.stopPropagation()} // 👈 prevent close on inside click
            >
                <Card className="ai-chat-card">
                    <div className="ai-chat-header">
                        <span className="heading-font">AI Chat</span>
                        <span
                            className="close-btn"
                            onClick={onClose}
                            style={{ cursor: onClose ? "pointer" : undefined }}
                        >
                            <IoClose />
                        </span>
                    </div>

                    <Card.Body className="ai-chat-body">
                        <div className="ai-chat-message-wrapper">
                            {messages.length === 0 && !loading && !initialLoading && (
                                <div className="ai-answer">
                                    Type your question and get AI-powered answers....!
                                </div>
                            )}

                            {messages.map(msg => (
                                <React.Fragment key={msg.id}>
                                    {msg.type === "question" && (
                                        <div className="user-message">
                                            {msg.text}
                                        </div>
                                    )}

                                    {msg.type === "answer" && (
                                        <div className="ai-answer">
                                            <div className="ai-answer-label">
                                                {aiIcon()} AI Answer
                                            </div>
                                            <div className="ai-answer-bubble">
                                                {msg.text}
                                            </div>
                                        </div>
                                    )}
                                </React.Fragment>
                            ))}

                            {loading && (
                                <div className="ai-loader">Thinking…</div>
                            )}

                            {initialLoading && (
                                <div className="ai-loader">Loading…</div>
                            )}

                            <div ref={chatEndRef} />
                        </div>
                    </Card.Body>

                    <div className="ai-chat-footer">
                        <InputGroup className="border rounded-pill p-1 gap-1">
                            <Form.Control
                                placeholder="What is my today's sale ?"
                                value={question}
                                onChange={(e) => setQuestion(e.target.value)}
                                onKeyDown={(e) => {
                                    if (e.key === "Enter" && !loading && !initialLoading && question.trim()) {
                                        e.preventDefault();
                                        handleSendMessage();
                                    }
                                }}
                                className="chat-input rounded-start-pill"
                            />
                            <Button
                                disabled={loading || initialLoading || !question.trim()}
                                className="send-btn"
                                onClick={handleSendMessage}
                            >
                                <IoIosSend size={20} className="text-white" />
                            </Button>
                        </InputGroup>
                    </div>
                </Card>
            </div>
        </div>

    );
};

export default AiChat;
